import React, { useState } from 'react';
import axios from 'axios';
import { mockData } from '../mock';
import { Logo } from '../components/Logo';
import { ServiceCard } from '../components/ServiceCard';
import { ProjectCard } from '../components/ProjectCard';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Phone, Mail, MapPin, Award, Zap, Sparkles, ChevronRight, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Home = () => {
  const { toast } = useToast();
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  
  const [callbackForm, setCallbackForm] = useState({
    name: '',
    phone: '',
    preferredTime: ''
  });

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/quotes`, quoteForm);
      
      toast({
        title: "Demande de devis envoyée !",
        description: "Nous vous recontacterons dans les 24h.",
      });
      
      setQuoteForm({ name: '', email: '', phone: '', projectType: '', message: '' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/callbacks`, callbackForm);
      
      toast({
        title: "Demande de rappel enregistrée !",
        description: "Nous vous rappellerons rapidement.",
      });
      
      setCallbackForm({ name: '', phone: '', preferredTime: '' });
    } catch (error) {
      console.error('Error submitting callback:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const valueIconMap = {
    Award,
    Zap,
    Sparkles
  };

  return (
    <div className="home-page">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b-2 border-slate-200 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('services')} className="text-slate-700 hover:text-blue-600 transition-colors font-medium">
              Services
            </button>
            <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-red-600 transition-colors font-medium">
              À propos
            </button>
            <button onClick={() => scrollToSection('projects')} className="text-slate-700 hover:text-blue-600 transition-colors font-medium">
              Réalisations
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-red-600 transition-colors font-medium">
              Contact
            </button>
            <Button onClick={() => scrollToSection('contact')} className="bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all">
              Devis gratuit
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-20 bg-gradient-to-br from-blue-50 via-white to-red-50 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1527148258266-a4d5b1469786?q=85" 
            alt="Audit énergétique" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <span className="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-md border-2 border-blue-200">
                <span className="w-2 h-2 bg-red-600 rounded-full mr-2 animate-pulse"></span>
                <span className="text-sm font-medium text-slate-700">Expert en Performance Énergétique</span>
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Optimisez la Performance <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-red-600">Énergétique</span> de Vos Bâtiments
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Cabinet expert en études thermiques et conseil énergétique. 
              Conformité réglementaire, économies d'énergie et valorisation de votre patrimoine.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => scrollToSection('contact')} size="lg" className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all">
                Demander un devis
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button onClick={() => scrollToSection('services')} size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all">
                Nos services
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto">
              {mockData.about.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className={`text-3xl md:text-4xl font-bold mb-2 ${idx % 2 === 0 ? 'text-blue-600' : 'text-red-600'}`}>{stat.number}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-100 text-red-600 rounded-full text-sm font-semibold mb-4">NOS EXPERTISES</span>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Nos Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une gamme complète de prestations pour tous vos projets énergétiques
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {mockData.services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">POURQUOI NOUS CHOISIR</span>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">{mockData.about.title}</h2>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
                {mockData.about.description}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {mockData.about.values.map((value, idx) => {
                const IconComponent = valueIconMap[value.icon];
                const isRed = idx % 2 === 1;
                return (
                  <Card key={idx} className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-300">
                    <CardHeader>
                      <div className={`w-16 h-16 ${isRed ? 'bg-red-100' : 'bg-blue-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                        <IconComponent className={`w-8 h-8 ${isRed ? 'text-red-600' : 'text-blue-600'}`} />
                      </div>
                      <CardTitle className="text-xl">{value.title}</CardTitle>
                      <CardDescription>{value.description}</CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-red-100 text-red-600 rounded-full text-sm font-semibold mb-4">PORTFOLIO</span>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Nos Réalisations</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des projets variés, une expertise reconnue
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {mockData.projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 to-red-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">CONTACTEZ-NOUS</span>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Parlons de Votre Projet</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Nous sommes à votre écoute pour répondre à tous vos besoins énergétiques
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Quote Form */}
            <Card className="shadow-xl border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-blue-600" />
                  </span>
                  Demande de Devis
                </CardTitle>
                <CardDescription>Recevez une estimation gratuite sous 24h</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleQuoteSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="quote-name">Nom complet *</Label>
                    <Input
                      id="quote-name"
                      required
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                      placeholder="Votre nom"
                      className="border-2 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-email">Email *</Label>
                    <Input
                      id="quote-email"
                      type="email"
                      required
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="border-2 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-phone">Téléphone *</Label>
                    <Input
                      id="quote-phone"
                      type="tel"
                      required
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                      placeholder="06 XX XX XX XX"
                      className="border-2 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-type">Type de projet *</Label>
                    <Input
                      id="quote-type"
                      required
                      value={quoteForm.projectType}
                      onChange={(e) => setQuoteForm({ ...quoteForm, projectType: e.target.value })}
                      placeholder="Ex: Étude RT2020, DPE, Audit..."
                      className="border-2 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quote-message">Message</Label>
                    <Textarea
                      id="quote-message"
                      value={quoteForm.message}
                      onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                      placeholder="Décrivez votre projet..."
                      rows={4}
                      className="border-2 focus:border-blue-500"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all">
                    Envoyer ma demande
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Callback Form + Contact Info */}
            <div className="space-y-6">
              <Card className="shadow-xl border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-4 h-4 text-red-600" />
                    </span>
                    Demande de Rappel
                  </CardTitle>
                  <CardDescription>Nous vous rappelons rapidement</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCallbackSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="callback-name">Nom *</Label>
                      <Input
                        id="callback-name"
                        required
                        value={callbackForm.name}
                        onChange={(e) => setCallbackForm({ ...callbackForm, name: e.target.value })}
                        placeholder="Votre nom"
                        className="border-2 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="callback-phone">Téléphone *</Label>
                      <Input
                        id="callback-phone"
                        type="tel"
                        required
                        value={callbackForm.phone}
                        onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
                        placeholder="06 XX XX XX XX"
                        className="border-2 focus:border-red-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="callback-time">Horaire préféré</Label>
                      <Input
                        id="callback-time"
                        value={callbackForm.preferredTime}
                        onChange={(e) => setCallbackForm({ ...callbackForm, preferredTime: e.target.value })}
                        placeholder="Ex: Matin, Après-midi..."
                        className="border-2 focus:border-red-500"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transition-all">
                      Demander un rappel
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle>Nos Coordonnées</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{mockData.company.address}</div>
                      <div className="text-slate-600">{mockData.company.postalCode} {mockData.company.city}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-slate-900">{mockData.company.email}</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <Logo className="logo-white" />
              </div>
              <p className="text-slate-400 text-sm">
                {mockData.company.tagline}
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-blue-400">Liens rapides</h3>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">À propos</button></li>
                <li><button onClick={() => scrollToSection('projects')} className="hover:text-white transition-colors">Réalisations</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-red-400">Contact</h3>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>{mockData.company.address}</li>
                <li>{mockData.company.postalCode} {mockData.company.city}</li>
                <li>{mockData.company.email}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400 text-sm">
            <p>&copy; {new Date().getFullYear()} SB Consulting. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
